import { DateTime } from "ts-luxon";
import {
  type HeatRegistration,
  type Match,
  type MatchDetail,
  type MatchDisplay,
  MatchStatus,
} from "../model/type";
import i18n from "i18next";

export function formatPlayerMatch(match: MatchDetail): string {
  const tableName = match.table ? `Table ${match.table.number}` : "";
  return `${tableName} ${formatMatch(match)}`;
}

export function formatPlayerMatchFull(match: MatchDetail): string {
  const tableName = match.table ? `Table ${match.table.number}` : "";
  const status = i18n.t(`MATCHSTATUS.${match.status}`);
  if (match.status === MatchStatus.FINISHED) {
    return `${tableName}, état du match : ${status}, durée : ${formatDuration(match.begin.toString(), match.end.toString())}`;
  }
  return `${tableName}, état du match : ${status}`;
}

export function formatMatchName(match: MatchDetail): string {
  if (match.type === "POULE") {
    return `Poule`;
  }
  if (match.type === "TABLEAU_FINAL") {
    if (match.round === 0) {
      return "Finale";
    } else {
      return `1/${2 ** match.round} finale`;
    }
  }
  return "";
}

export function displayRound(match: Match): string {
  if (match.type === "POULE") {
    return i18n.t("match.group.name");
  }
  const label = match.round === 0 ? "" : "1/" + Math.pow(2, match.round) + " ";
  return label + i18n.t("match.round.name");
}

export function displayRoundDetail(match: MatchDetail): string {
  if (match.type === "POULE") {
    return i18n.t("match.group.name");
  }
  const label = match.round === 0 ? "" : "1/" + Math.pow(2, match.round) + " ";
  return label + i18n.t("match.round.name");
}

export function formatMatch(match: MatchDetail): string {
  var duration = "";
  if (match.status === MatchStatus.PENDING) {
    duration = formatDuration(
      match.dateCreated.toString(),
      new Date().toISOString(),
    );
    return `${match.heatName} (${formatMatchName(match)}) en attente depuis ${duration}`;
  }
  if (match.status === MatchStatus.RUNNING) {
    duration = formatDuration(match.begin.toString(), new Date().toISOString());
    return `${match.heatName} (${formatMatchName(match)}) en cours depuis ${duration}`;
  }
  if (match.status === MatchStatus.FINISHED) {
    duration = formatDuration(match.end.toString(), new Date().toISOString());
    return `${match.heatName} (${formatMatchName(match)}) terminé depuis ${duration}`;
  }
  return `${match.heatName} ${formatMatchName(match)} en cours depuis ${duration}`;
}

function formatDuration(begin: string, end: string) {
  const date1 = DateTime.fromISO(begin);
  const date2 = DateTime.fromISO(end);

  const diff = date2.diff(date1, ["hours", "minutes"]).toObject();

  const hours = Math.floor(diff.hours ?? 0);
  const minutes = Math.floor(diff.minutes ?? 0);

  if (hours > 0) {
    return `${hours}h${minutes}m`;
  }
  return `${minutes}m`;
}

export const formatDateLiteral = (date: string): string => {
  if (!date) return "";

  return DateTime.fromISO(date)
    .setLocale(navigator.language) // locale navigateur
    .toLocaleString(DateTime.DATE_HUGE);
};

export function formatPlayers(registration: HeatRegistration) {
  const players = registration.players;
  if (players.length === 1) {
    return (
      players[0].lastname.toUpperCase() +
      " " +
      players[0].firstname +
      " (" +
      (registration.ranking ?? "-") +
      ")   [" +
      (players[0].club ? players[0].club?.name : "-") +
      "]"
    );
  }
  if (players.length === 2) {
    return (
      players[0].lastname.toUpperCase() +
      " " +
      players[0].firstname.charAt(0) +
      ". - " +
      players[1].lastname.toUpperCase() +
      " " +
      players[1].firstname.charAt(0) +
      "."
    );
  }
  return "";
}

export function formatPlayersSimple(registration: HeatRegistration) {
  const players = registration.players;
  if (players.length === 1) {
    return (
      players[0].lastname.toUpperCase() +
      " " +
      players[0].firstname +
      " (" +
      (registration.ranking ?? "-") +
      ")"
    );
  }
  if (players.length === 2) {
    return (
      players[0].lastname.toUpperCase() +
      " " +
      players[0].firstname.charAt(0) +
      ". - " +
      players[1].lastname.toUpperCase() +
      " " +
      players[1].firstname.charAt(0) +
      "."
    );
  }
  return "";
}

export function formatPlayersForBracket(registration: HeatRegistration) {
  const players = registration.players;
  if (players.length === 1) {
    return (
      players[0].lastname.toUpperCase() +
      " " +
      players[0].firstname +
      " (" +
      (players[0].points ?? "-") +
      ")"
    );
  }
  if (players.length === 2) {
    return (
      players[0].lastname.toUpperCase() +
      " " +
      players[0].firstname.charAt(0) +
      ". - " +
      players[1].lastname.toUpperCase() +
      " " +
      players[1].firstname.charAt(0) +
      "."
    );
  }
  return "";
}

export function computeMatchColor(match: MatchDetail) {
  if (match.status === MatchStatus.RUNNING) {
    return "match-in-progress";
  }
  if (match.status === MatchStatus.PENDING) {
    if (match.playersFree) {
      return "match-finished";
    }
    return "match-pending";
  }
  return "";
}

export function computeQualified(match: Match): number {
  if (match.type === "TABLEAU_FINAL") {
    return 1;
  }
  if (match.type === "POULE") {
    return 2;
  }
  return 1;
}

export function haveSameClub(match: MatchDisplay) {
  const hasDuplicate =
    match?.opponents &&
    new Set(match.opponents.map((e) => e.club)).size !== match.opponents.length;
  return hasDuplicate;
}
